<template>
    <div class="create">
        <portal to="title">Create enquiry</portal>

        <transition-group name="fade">
            <vue-form-card v-for="(patient, index) in form.patients" :key="`patient-form-${index}`">
                <template v-slot:title>Patient Details</template>

                <template v-slot:description>
                    Please be assured that this information will be private and stored securely
                </template>

                <template v-slot:body>
                    <vue-select-input
                        class="my-5"
                        label="Existing patient"
                        :data="patients"
                        :loading="loading.patients"
                        :data-value="(item) => item.id"
                        :data-text="(item) => `${item.first_name} ${item.last_name}`"
                        v-on:change="setPatient(index, $event)"
                        placeholder="Select an existing patient or fill the form"
                    />

                    <div class="form-row mb-3">
                        <vue-text-input
                            class="col-lg-6 col-md-12 col-sm-12 pr-2 mb-5 mb-lg-0"
                            label="First name"
                            :required="true"
                            :errors="formErrors[`patients.${index}.firstName`]"
                            v-model="patient.firstName"
                        />
                        <vue-text-input
                            class="col-lg-6 col-md-12 col-sm-12 pl-2"
                            label="Last name"
                            :required="true"
                            :errors="formErrors[`patients.${index}.lastName`]"
                            v-model="patient.lastName"
                        />
                    </div>

                    <vue-text-input
                        class="mb-5"
                        label="Date of birth"
                        placeholder="mm/dd/yyyy"
                        type="date"
                        :required="true"
                        :errors="formErrors[`patients.${index}.dateOfBirth`]"
                        v-model="patient.dateOfBirth"
                    />

                    <vue-textarea-input
                        :class="{ 'mb-5': index == form.patients.length - 1 }"
                        label="Please set out relevant information about this patient's condition"
                        :required="true"
                        :errors="formErrors[`patients.${index}.additionalInfo`]"
                        v-model="patient.additionalInfo"
                    />

                    <button
                        class="btn btn-primary my-2 w-100"
                        type="button"
                        v-on:click="addPatient"
                        v-if="index == form.patients.length - 1"
                    >
                        Add another patient
                    </button>

                    <button
                        class="btn btn-danger my-2 w-100"
                        type="button"
                        v-on:click="form.patients.splice(index, 1)"
                        v-if="index != 0"
                    >
                        Remove patient
                    </button>
                </template>
            </vue-form-card>
        </transition-group>

        <vue-form-card class="mt-10">
            <template v-slot:title>Service Options</template>

            <template v-slot:description>
                Please be assured that this information will be private and stored securely
            </template>

            <template v-slot:body>
                <div class="row mt-5 mb-3">
                    <div class="col-12 col-md-6">
                        <vue-select-input
                            label="Discipline"
                            :loading="loading.disciplines"
                            :data="disciplines"
                            :data-value="(item) => item.id"
                            :data-text="(item) => item.name"
                            v-on:page-changed="disciplinesPaginator.page = $event"
                            v-on:search="searchDisciplines"
                            v-model="form.disciplineId"
                            :required="true"
                            :errors="formErrors['disciplineId']"
                        />
                    </div>

                    <div class="col-12 col-md-6">
                        <vue-check-group-input
                            class="mt-0 mt-md-6"
                            :radio="true"
                            v-model="form.disciplineType"
                            :data="disciplineTypes"
                            :errors="formErrors['disciplineType']"
                        />
                    </div>
                </div>

                <transition name="fade">
                    <vue-check-group-input
                        label="If optional, do you want a report?"
                        :radio="true"
                        v-model="form.disciplineReport"
                        :data="disciplineReport"
                        v-show="form.disciplineType == 'assessment'"
                        :errors="formErrors['disciplineReport']"
                    />
                </transition>

                <small class="text-muted">
                    An assessment is the necessary first step. However, you can start with therapy if the patient has
                    already been assessed by an appropiate therapist and has:
                    <br />
                    (a) a written report from within the last 6 months, or
                    <br />
                    (b) a set of current targets
                </small>
            </template>
        </vue-form-card>

        <vue-form-card class="mt-10">
            <template v-slot:title>Preferred place for the appointment</template>

            <template v-slot:description>
                Please be assured that this information will be private and stored securely
            </template>

            <template v-slot:body>
                <vue-check-group-input
                    class="mt-0 mt-md-6"
                    :radio="true"
                    v-model="form.appointmentPlace"
                    :data="appointmentPlaces"
                    :errors="formErrors['appointmentPlace']"
                />

                <transition name="fade">
                    <small class="highlight-text" v-show="form.appointmentPlace == 'none'">
                        Our therapist will suggest a location for your appointment
                    </small>
                </transition>

                <transition name="fade">
                    <div class="address-inputs mt-5" v-show="form.appointmentPlace == 'other'">
                        <vue-text-input
                            label="Postal code"
                            class="mb-5"
                            :required="true"
                            v-model="form.appointmentAddress.postalCode"
                            :loading="loading.postalCode"
                            :errors="formErrors['appointmentAddress.postalCode']"
                        />

                        <vue-text-input
                            label="Street address"
                            class="mb-5"
                            :required="true"
                            v-model="form.appointmentAddress.line1"
                            placeholder="Street and number, P.O.box, c/o"
                            :errors="formErrors['appointmentAddress.line1']"
                        />

                        <vue-text-input
                            label="Street address 2"
                            class="mb-5"
                            v-model="form.appointmentAddress.line2"
                            placeholder="Flat, suite, unit, building, floor, etc"
                            :errors="formErrors['appointmentAddress.line2']"
                        />

                        <div class="row">
                            <div class="col-12 col-md-6">
                                <vue-text-input
                                    class="mb-5 mb-md-0"
                                    label="City"
                                    :required="true"
                                    v-model="form.appointmentAddress.city"
                                    :errors="formErrors['appointmentAddress.city']"
                                />
                            </div>
                            <div class="col-12 col-md-6">
                                <vue-select-input
                                    label="County"
                                    :loading="loading.counties"
                                    :data="counties"
                                    :data-value="(item) => item.id"
                                    :data-text="(item) => item.name"
                                    v-on:page-changed="countiesPaginator.page = $event"
                                    v-on:search="searchCounties"
                                    v-model="form.appointmentAddress.county"
                                    :errors="formErrors['appointmentAddress.county']"
                                />
                            </div>
                        </div>
                    </div>
                </transition>
            </template>
        </vue-form-card>

        <vue-form-card v-for="(date, index) in form.appointmentDates" :key="`date-form-${index}`">
            <template v-slot:title>Preferred date of appointment</template>

            <template v-slot:description>
                Please be assured that this information will be private and stored securely
            </template>

            <template v-slot:body>
                <div class="row mt-5">
                    <div class="col-12 col-md-6">
                        <vue-text-input
                            class="mb-5"
                            label="Preferred date"
                            placeholder="mm/dd/yyyy"
                            type="date"
                            v-model="date.date"
                        />
                    </div>
                    <div class="col-12 col-md-6">
                        <vue-check-group-input
                            class="mt-0 mt-md-6"
                            :radio="true"
                            v-model="date.period"
                            :data="appointmentDates"
                        />
                    </div>
                </div>

                <small class="text-muted">
                    If you are flexible, do not specify a date or time. This will improve your chances of finding an
                    available therapist
                </small>

                <button
                    class="btn btn-primary my-2 w-100"
                    type="button"
                    v-on:click="addDate"
                    v-if="index == form.appointmentDates.length - 1"
                >
                    Add another preferred date
                </button>

                <button
                    class="btn btn-danger my-2 w-100"
                    type="button"
                    v-on:click="form.appointmentDates.splice(index, 1)"
                    v-if="index != 0"
                >
                    Remove date
                </button>
            </template>
        </vue-form-card>

        <vue-form-card class="mt-10">
            <template v-slot:title>Additional information</template>

            <template v-slot:description>
                Please be assured that this information will be private and stored securely
            </template>

            <template v-slot:body>
                <vue-textarea-input
                    class="mt-5"
                    label="If necessary, set out additional relevant information here"
                    v-model="form.additionalInfo"
                />
            </template>
        </vue-form-card>

        <div class="text-center">
            <vue-check-input :check-value="true" v-model="form.agrees" label-class="form-check-label small text-muted">
                <template v-slot:label>
                    I agree with
                    <a :href="`${frontendUrl}/privacy-policy`">Privacy Policy</a>
                    and
                    <a :href="`${frontendUrl}/terms-and-conditions`">Terms & Conditions</a>
                </template>
            </vue-check-input>

            <small
                class="text-danger font-italic d-block"
                style="font-size: 12px"
                v-if="formErrors && formErrors.agrees"
            >
                {{ formErrors.agrees[0] }}
            </small>
        </div>

        <div class="mb-5 text-center py-8">
            <button class="btn btn-outline-primary mr-4" type="button" v-on:click="$router.back()">Cancel</button>
            <button type="button" class="btn btn-primary w-50" v-on:click="create" :disabled="loading.create">
                <i class="fa fa-spinner fa-spin" v-if="loading.create"></i>
                Save
            </button>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';

    import VueFormCard from '@components/formCard';
    import VueTextInput from '@components/textInput';
    import VueTextareaInput from '@components/textareaInput';
    import VueSelectInput from '@components/selectInput';
    import VueCheckGroupInput from '@components/checkGroupInput';
    import VueCheckInput from '@components/checkInput';

    import patientsService from '@api/patients';
    import disciplinesService from '@api/disciplines';
    import enquiriesService from '@api/enquiries';
    import addressesService from '@api/addresses';
    import clientsService from '@api/clients';

    import { debounce } from 'lodash';

    export default Vue.extend({
        components: {
            VueFormCard,
            VueTextInput,
            VueTextareaInput,
            VueSelectInput,
            VueCheckGroupInput,
            VueCheckInput
        },
        data() {
            return {
                patients: {},
                form: {
                    patients: [],
                    disciplineId: null,
                    disciplineType: 'assessment',
                    disciplineReport: 'yes',
                    appointmentPlace: 'same',
                    appointmentAddress: addressesService.defaultInput(),
                    appointmentDates: [],
                    additionalInfo: '',
                    agrees: false
                },
                formErrors: {},
                disciplines: {},
                counties: {},
                disciplinesPaginator: disciplinesPaginator(),
                countiesPaginator: countiesPaginator(),
                disciplineTypes: disciplineTypes(),
                disciplineReport: disciplineReport(),
                appointmentPlaces: appointmentPlaces(),
                appointmentDates: appointmentDates(),
                frontendUrl: window.app.frontendUrl,
                loading: {
                    disciplines: true,
                    counties: true,
                    patients: true,
                    postalCode: false,
                    create: false
                }
            };
        },
        watch: {
            disciplinesPaginator: {
                deep: true,
                handler() {
                    this.fetchDisciplines();
                }
            },
            countiesPaginator: {
                deep: true,
                handler() {
                    this.fetchCounties();
                }
            },
            'form.appointmentAddress.postalCode'() {
                this.checkPostalCode();
            }
        },
        methods: {
            fetchPatients() {
                this.loading.patients = true;

                patientsService
                    .index()
                    .then((response) => {
                        this.patients = response.data.data;
                    })
                    .catch((error) => {
                        window.location.href = `/error/${error.response.status}`;
                    })
                    .finally(() => {
                        this.loading.patients = false;
                    });
            },
            fetchDisciplines() {
                this.loading.disciplines = true;

                disciplinesService
                    .index(this.disciplinesPaginator)
                    .then((response) => {
                        this.disciplines = response.data;
                    })
                    .catch((error) => {
                        window.location.href = `/error/${error.response.status}`;
                    })
                    .finally(() => {
                        this.loading.disciplines = false;
                    });
            },
            fetchCounties() {
                this.loading.counties = true;

                addressesService
                    .counties(this.countiesPaginator)
                    .then((response) => {
                        this.counties = response.data;
                    })
                    .catch((error) => {
                        window.location.href = `/error/${error.response.status}`;
                    })
                    .finally(() => {
                        this.loading.counties = false;
                    });
            },
            addPatient() {
                this.form.patients.push(patientInput());
            },
            addDate() {
                this.form.appointmentDates.push(appointmentDateInput());
            },
            create() {
                this.formErrors = {};
                this.loading.create = true;

                enquiriesService
                    .create(this.form)
                    .then((response) => {
                        this.$router.push(`/enquiries/${response.data.id}`);
                    })
                    .catch((error) => {
                        if (error.response.data.errors) {
                            this.formErrors = error.response.data.errors;
                            this.$toasted.error(
                                'Please enter a valid value in all the required fields before proceeding.',
                                { icon: 'exclamation-triangle' }
                            );
                            this.$goToError();
                        } else {
                            window.location.href = `/error/${error.response.status}`;
                        }
                    })
                    .finally(() => {
                        this.loading.create = false;
                    });
            },
            setPatient(index, patient) {
                const dbPatient = this.patients.find((p) => p.id == patient);
                const localPatient = this.form.patients[index];

                localPatient.id = patient;
                localPatient.firstName = dbPatient.first_name;
                localPatient.lastName = dbPatient.last_name;
                localPatient.dateOfBirth = dbPatient.date_of_birth;
                localPatient.additionalInfo = dbPatient.additional_info;
            },
            searchDisciplines: debounce(function(term) {
                this.disciplinesPaginator.page = 1;
                this.disciplinesPaginator.term = term;
            }, 500),
            searchCounties: debounce(function(term) {
                this.countiesPaginator.page = 1;
                this.countiesPaginator.term = term;
            }, 500),
            checkPostalCode: debounce(function() {
                if (this.formErrors && this.formErrors['appointmentAddress.postalCode']) {
                    this.$delete(this.formErrors, 'appointmentAddress.postalCode');
                }

                this.form.appointmentAddress.latitude = null;
                this.form.appointmentAddress.longitude = null;
                this.loading.postalCode = true;

                addressesService
                    .checkPostalCode(this.form.appointmentAddress.postalCode)
                    .then((response) => {
                        this.form.appointmentAddress.latitude = response.data.result.latitude;
                        this.form.appointmentAddress.longitude = response.data.result.longitude;
                    })
                    .catch((error) => {
                        this.$set(this.formErrors, 'appointmentAddress.postalCode', [
                            'The provided postal code is invalid'
                        ]);
                    })
                    .finally(() => {
                        this.loading.postalCode = false;
                    });
            }, 500)
        },
        created() {
            this.addPatient();
            this.addDate();
        },
        mounted() {
            if (clientsService.enquiryReady()) {
                this.$toasted.error('Please fill all the client required fields before creating an enquiry.', {
                    icon: 'exclamation-triangle'
                });

                this.$router.push('/clients/edit');
            }

            this.fetchPatients();
            this.fetchDisciplines();
            this.fetchCounties();
        }
    });

    /** Options */
    const disciplineTypes = () => [
        { id: 'assessment', text: 'Assessment' },
        { id: 'therapy', text: 'Therapy' },
        { id: 'meeting', text: 'Meeting' }
    ];
    const disciplineReport = () => [
        { id: 'yes', text: 'Yes' },
        { id: 'no', text: 'No' }
    ];
    const appointmentPlaces = () => [
        { id: 'same', text: 'Client address' },
        { id: 'other', text: 'Other address' },
        { id: 'venue', text: 'Owl Centre venue (if available)' },
        { id: 'online', text: 'Online' },
        { id: 'none', text: 'No preference' }
    ];
    const appointmentDates = () => [
        { id: 'morning', text: 'Morning' },
        { id: 'afternoon', text: 'Afternoon' },
        { id: 'any', text: 'Any time' }
    ];

    /** Paginators */
    const disciplinesPaginator = () => {
        return {
            term: null,
            page: 1
        };
    };

    const countiesPaginator = () => {
        return {
            term: null,
            page: 1,
            all: true
        };
    };

    /** Inputs */
    const patientInput = () => {
        return {
            id: null,
            firstName: '',
            lastName: '',
            dateOfBirth: '',
            additionalInfo: ''
        };
    };

    const appointmentDateInput = () => {
        return {
            id: null,
            date: '',
            period: 'any'
        };
    };
</script>
